/**
 * Agent Status Manager
 * Handles agent availability status, queue connection, and incoming call notifications
 */

import React, { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../../config/config';
import { buildRecordUrl } from '../../routes';
// Phase 1+2+3 chat shared components (owner-spec 2026-05-31).
import { renderMedia, buildMediaList, buildReplyTarget } from './chat/MessageBubble';
import MediaLightbox from './chat/MediaLightbox';
import MediaLibraryDrawer from './chat/MediaLibraryDrawer';
import ChatSearchBar from './chat/ChatSearchBar';
import ReplyQuote from './chat/ReplyQuote';
import ReplyComposerBar from './chat/ReplyComposerBar';
import MessageActionsMenu from './chat/MessageActionsMenu';
import MessageInfoModal from './chat/MessageInfoModal';
// Same reader the record tab and the communication centre use. The local
// copy this replaced was character-identical to the record tab's and
// carried the same parseInt-on-a-string defect.
import { toEpochMs } from './utils/waMessageTime';
// Lazy load AccountModal
const AccountModal = lazy(() => import('../modals/AccountModal'));

// All WhatsApp endpoints now served by main API (Infobip integration)
const API_BASE = config.api.baseUrl;
const MAIN_API_BASE = config.api.baseUrl;

// LocalStorage keys for persistence
const STORAGE_KEYS = {
  INCOMING_CALLS: 'whatsapp_incoming_calls',
  MINIMIZED_CHAT: 'whatsapp_minimized_chat',
  RECENT_CHATS: 'whatsapp_recent_chats'
};

/**
 * ⭐ 2026-09-04, Baileys removal stage 2.
 *
 * The Node/Baileys service that held the agent-notification queue died on
 * 2026-07-06 and is disabled at boot. Everything below that talked to it is
 * guarded by this constant rather than deleted, matching stage 1 on the
 * backend — stage 3 removes both sides and `WHATSAPP_USE_BAILEYS` together.
 *
 * A named constant, not a bare `return`: a literal-false guard reads as
 * unreachable code to eslint and CRA fails the production build on it.
 */
const BAILEYS_QUEUE_ALIVE = false;

const AgentStatusManager = ({ currentUser, onNavigate }) => {
  const navigate = useNavigate();
  const isAdmin = currentUser?.isAdmin || currentUser?.profile === 'system_admin' || currentUser?.profile === 'מנהל מערכת' || currentUser?.profile === 'Admin' || currentUser?.Username === 'admin@afikim.local' || currentUser?.Username === 'meni@afikim.my';
  const [isConnected, setIsConnected] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showIncomingPopup, setShowIncomingPopup] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [incomingCalls, setIncomingCalls] = useState([]);
  const [currentIncoming, setCurrentIncoming] = useState(null);
  const [userQueues, setUserQueues] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [modalChatData, setModalChatData] = useState(null);
  const [minimizedChat, setMinimizedChat] = useState(null); // For minimized active chat
  const [showRecentChatsModal, setShowRecentChatsModal] = useState(false); // Recent chats modal
  const [recentChats, setRecentChats] = useState([]); // All recent chats with status
  const [panelTab, setPanelTab] = useState('new'); // 'new' or 'mywork'
  const [accountSearching, setAccountSearching] = useState({}); // Track which chats are searching
  const [chatAccountResults, setChatAccountResults] = useState({}); // Account results per chatId
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const wsRef = useRef(null);
  const pollingRef = useRef(null);

  // Load persisted data from localStorage on mount
  useEffect(() => {
    try {
      const savedIncomingCalls = localStorage.getItem(STORAGE_KEYS.INCOMING_CALLS);
      if (savedIncomingCalls) {
        const calls = JSON.parse(savedIncomingCalls);
        // Filter out calls older than 24 hours
        const validCalls = calls.filter(call => {
          const callTime = new Date(call.timestamp).getTime();
          return Date.now() - callTime < 24 * 60 * 60 * 1000;
        });
        setIncomingCalls(validCalls);
      }

      const savedMinimizedChat = localStorage.getItem(STORAGE_KEYS.MINIMIZED_CHAT);
      if (savedMinimizedChat) {
        const chat = JSON.parse(savedMinimizedChat);
        // Only restore if less than 24 hours old
        if (chat.timestamp && Date.now() - new Date(chat.timestamp).getTime() < 24 * 60 * 60 * 1000) {
          setMinimizedChat(chat);
        }
      }

      const savedRecentChats = localStorage.getItem(STORAGE_KEYS.RECENT_CHATS);
      if (savedRecentChats) {
        setRecentChats(JSON.parse(savedRecentChats));
      }
    } catch (err) {
      console.error('[AGENT] Error loading from localStorage:', err);
    }
  }, []);

  // Save incoming calls to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.INCOMING_CALLS, JSON.stringify(incomingCalls));
    } catch (err) {
      console.error('[AGENT] Error saving incoming calls:', err);
    }
  }, [incomingCalls]);

  // Save minimized chat to localStorage whenever it changes
  useEffect(() => {
    try {
      if (minimizedChat) {
        localStorage.setItem(STORAGE_KEYS.MINIMIZED_CHAT, JSON.stringify(minimizedChat));
      } else {
        localStorage.removeItem(STORAGE_KEYS.MINIMIZED_CHAT);
      }
    } catch (err) {
      console.error('[AGENT] Error saving minimized chat:', err);
    }
  }, [minimizedChat]);

  // Save recent chats to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.RECENT_CHATS, JSON.stringify(recentChats));
    } catch (err) {
      console.error('[AGENT] Error saving recent chats:', err);
    }
  }, [recentChats]);

  // Add chat to recent chats when answered
  const addToRecentChats = useCallback((chatData, status = 'active') => {
    const now = new Date().toISOString();
    setRecentChats(prev => {
      // Check if chat already exists
      const existingIndex = prev.findIndex(c => c.chatId === chatData.chatId);

      // Calculate response time if this is a new answer to an existing waiting call
      let responseTimeMs = null;
      let inquiryTime = chatData.inquiryTime || now;

      if (existingIndex >= 0) {
        // Keep original inquiry time
        inquiryTime = prev[existingIndex].inquiryTime || inquiryTime;
        // Calculate response time only on first answer
        if (!prev[existingIndex].agentResponseTime && status === 'active') {
          responseTimeMs = new Date(now).getTime() - new Date(inquiryTime).getTime();
        }
      }

      const chatEntry = {
        chatId: chatData.chatId,
        customerName: chatData.customerName,
        customerPhone: chatData.customerPhone,
        startTime: existingIndex >= 0 ? prev[existingIndex].startTime : now,
        lastActivity: now,
        status: status, // 'active', 'ended', 'waiting'
        accountId: chatData.accountId,
        accountName: chatData.accountName,
        // Timestamp fields for reporting
        inquiryTime: inquiryTime, // When customer first contacted
        agentResponseTime: existingIndex >= 0 && prev[existingIndex].agentResponseTime
          ? prev[existingIndex].agentResponseTime
          : (status === 'active' ? now : null), // When agent first responded
        responseTimeMs: existingIndex >= 0 && prev[existingIndex].responseTimeMs
          ? prev[existingIndex].responseTimeMs
          : responseTimeMs, // Time in ms between inquiry and response
        agentId: chatData.agentId || currentUser?.email,
        agentName: chatData.agentName || currentUser?.firstName || currentUser?.name,
        wasTransferred: chatData.wasTransferred || false, // Track if chat was transferred back to queue
        queueId: chatData.queueId,
        queueName: chatData.queueName
      };

      if (existingIndex >= 0) {
        // Update existing
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], ...chatEntry };
        return updated;
      } else {
        // Add new (limit to 100 recent chats for reporting)
        return [chatEntry, ...prev].slice(0, 100);
      }
    });
  }, [currentUser]);

  // End chat - mark as ended
  const handleEndChat = useCallback((chatId) => {
    setRecentChats(prev => prev.map(chat =>
      chat.chatId === chatId
        ? { ...chat, status: 'ended', endTime: new Date().toISOString() }
        : chat
    ));
    // Also remove from minimized if it's the minimized chat
    if (minimizedChat?.chatId === chatId) {
      setMinimizedChat(null);
    }
    showToastMessage('השיחה הסתיימה');
  }, [minimizedChat]);

  // Check if chat can be reopened (within 24 hours of start)
  const canReopenChat = useCallback((chat) => {
    if (!chat.startTime) return false;
    const startTime = new Date(chat.startTime).getTime();
    return Date.now() - startTime < 24 * 60 * 60 * 1000;
  }, []);

  // Reopen chat or start new
  const handleReopenChat = useCallback((chat) => {
    if (canReopenChat(chat)) {
      // Reopen existing chat
      setRecentChats(prev => prev.map(c =>
        c.chatId === chat.chatId
          ? { ...c, status: 'active', lastActivity: new Date().toISOString() }
          : c
      ));
      setModalChatData({
        chatId: chat.chatId,
        customerName: chat.customerName,
        customerPhone: chat.customerPhone,
        accountId: chat.accountId,
        accountName: chat.accountName
      });
      setShowChatModal(true);
      setShowRecentChatsModal(false);
    } else {
      // Start new chat (24 hours passed)
      showToastMessage('עברו 24 שעות - יש לפתוח שיחה חדשה');
    }
  }, [canReopenChat]);

  // Search for Accounts by phone number - returns array of all matching accounts
  const searchAccountsByPhone = async (phone) => {
    if (!phone) return [];

    // Clean phone — extract only digits.
    let cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length < 7) return [];

    // 2026-05-31 owner-flagged: WhatsApp/Infobip delivers phones as
    // 972NNNNNNNNN (E.164 without "+"), but every Account in PG was
    // imported from Salesforce with the Israeli local format 0NNNNNNNNN.
    // The backend's /api/postgres/search/phone DOES normalise 972→0
    // server-side, but normalising on the FE too:
    //   (a) is belt-and-braces if the BE deploy lags,
    //   (b) makes the URL show the local form in the console, which is
    //       what the owner inspects when debugging "why didn't it find me?".
    if (cleanPhone.startsWith('972') && cleanPhone.length >= 10) {
      cleanPhone = '0' + cleanPhone.slice(3);
    }

    try {
      console.log('[AGENT] Searching Accounts for phone:', cleanPhone);
      const response = await fetch(`${MAIN_API_BASE}/api/postgres/search/phone?phone=${encodeURIComponent(cleanPhone)}&limit=10`);
      const data = await response.json();

      console.log('[AGENT] Phone search result:', data);

      const results = [];

      // Add all matching Accounts
      if (data.results?.Account?.length > 0) {
        for (const account of data.results.Account) {
          results.push({
            id: account.Id,
            name: account.Name,
            phone: account.Phone,
            type: 'Account'
          });
        }
      }

      // Add all matching Contacts
      if (data.results?.Contact?.length > 0) {
        for (const contact of data.results.Contact) {
          results.push({
            id: contact.Id,
            name: `${contact.FirstName || ''} ${contact.LastName || ''}`.trim(),
            phone: contact.Phone || contact.MobilePhone,
            accountId: contact.AccountId,
            type: 'Contact'
          });
        }
      }

      console.log('[AGENT] Found matches:', results.length);
      return results;
    } catch (err) {
      console.error('[AGENT] Phone search error:', err);
      return [];
    }
  };

  // Lookup account for a chat and cache results
  const lookupAccountForChat = useCallback(async (chatId, phone) => {
    if (chatAccountResults[chatId] || accountSearching[chatId]) return;
    setAccountSearching(prev => ({ ...prev, [chatId]: true }));
    try {
      const results = await searchAccountsByPhone(phone);
      setChatAccountResults(prev => ({ ...prev, [chatId]: results }));
    } catch (err) {
      console.error('[AGENT] Account lookup error:', err);
      setChatAccountResults(prev => ({ ...prev, [chatId]: [] }));
    } finally {
      setAccountSearching(prev => ({ ...prev, [chatId]: false }));
    }
  }, [chatAccountResults, accountSearching]);

  // Load user's assigned queues
  const loadUserQueues = useCallback(async () => {
    if (!currentUser?.email) return;

    try {
      const response = await fetch(`${API_BASE}/api/whatsapp/queues`);
      const data = await response.json();
      const queues = Array.isArray(data) ? data : (data.queues || []);

      // Filter queues where user is a member
      const userEmail = currentUser.email;
      const assignedQueues = queues.filter(q =>
        q.isActive && q.members?.includes(userEmail)
      );

      setUserQueues(assignedQueues);

      // Auto-connect if user is assigned to any queue
      if (assignedQueues.length > 0) {
        const savedStatus = localStorage.getItem(`agent_status_${userEmail}`);
        if (savedStatus !== 'disconnected') {
          handleConnect(true);
        }
      }
    } catch (err) {
      console.error('Error loading queues:', err);
    }
  }, [currentUser]);

  // Fetch last conversation info for a chat
  const fetchLastConversationInfo = async (chatId) => {
    try {
      const response = await fetch(`${API_BASE}/api/whatsapp/messages/${chatId}`);
      const data = await response.json();
      if (data.success && data.messages?.length > 0) {
        const msgs = data.messages;
        // Find last outgoing message from agent
        const agentMessages = msgs.filter(m => m.fromMe && m.sentByName);
        if (agentMessages.length > 0) {
          const lastAgentMsg = agentMessages[agentMessages.length - 1];
          return {
            lastConversationDate: toEpochMs(lastAgentMsg.timestamp),
            lastAgentName: lastAgentMsg.sentByName || 'נציג'
          };
        }
      }
      return null;
    } catch (err) {
      console.error('Error fetching conversation info:', err);
      return null;
    }
  };

// Poll for incoming notifications via HTTP
  const pollNotifications = useCallback(async () => {
    if (!currentUser?.email) return;
    // 2026-06-21 — Page Visibility guard. With many tabs left open across
    // the office, this 3s poller compounded into 39+ req/sec from a single
    // gateway IP. Skip when tab not visible; on focus, the next interval
    // tick re-checks. SSE/Socket push still works for foreground tabs.
    if (typeof document !== 'undefined' && document.hidden) {
      if (typeof window !== 'undefined') {
        window.__pollSkipCount = (window.__pollSkipCount || 0) + 1;
      }
      return;
    }
    // ⭐ 2026-09-04, Baileys removal stage 2 — owner: "they don't use that
    // button any more; active or not, they work through the Workspace."
    //
    // This polled the Node/Baileys agent-notification queue every 30s. That
    // service died on 2026-07-06, so the queue has been empty for two months
    // and every poll was a refused connection logged as an error — the noise
    // that made the backend log unreadable. Agent notifications reach the
    // Workspace over socket.io instead.
    //
    // Stopped rather than deleted, matching stage 1 on the backend: stage 3
    // removes both sides and the flag together.
    if (!BAILEYS_QUEUE_ALIVE) return;
    try {
      const response = await fetch(`${API_BASE}/api/whatsapp/agent/notifications?agentId=${encodeURIComponent(currentUser.email)}`);
      const data = await response.json();
      console.log('[AGENT POLL] Got notifications:', data.notifications?.length || 0);
      if (data.success && data.notifications?.length > 0) {
        for (const notification of data.notifications) {
          if (notification.type === 'incoming_message') {
            // Use functional state update to properly check for existing calls
            setIncomingCalls(prev => {
              const existingCall = prev.find(c => c.chatId === notification.chatId);

              if (existingCall) {
                // Update existing call with new message - NO popup, just update
                console.log('[AGENT POLL] Updating existing call for chatId:', notification.chatId);
                return prev.map(c => c.chatId === notification.chatId ? {
                  ...c,
                  message: notification.message,
                  timestamp: new Date().toISOString(),
                  messageCount: (c.messageCount || 1) + 1
                } : c);
              }

              // Also check if this chat is currently active (in recent chats as active)
              // to avoid showing popup for active conversations
              return prev; // Return unchanged for now, we'll add new call below
            });

            // Check synchronously if we need to add new call
            // We need to check current state outside of setState
            setIncomingCalls(prev => {
              const existingCall = prev.find(c => c.chatId === notification.chatId);
              if (existingCall) {
                return prev; // Already exists, don't add
              }

              // Create new incoming call entry
              console.log('[AGENT POLL] Adding NEW incoming call for chatId:', notification.chatId);
              const inquiryTime = new Date().toISOString();
              const incoming = {
                id: Date.now() + Math.random(),
                chatId: notification.chatId,
                customerName: notification.customerName || 'לקוח',
                customerPhone: notification.customerPhone,
                message: notification.message,
                timestamp: inquiryTime,
                inquiryTime: inquiryTime,
                queueId: notification.queueId,
                queueName: notification.queueName,
                messageCount: 1,
                matchingAccounts: [], // Will be populated async
                lastConversationDate: null, // Will be populated async
                lastAgentName: null // Will be populated async
              };

              // Show popup and play sound for NEW calls only
              setCurrentIncoming(incoming);
              setShowIncomingPopup(true);
              playNotificationSound();

              // Search for accounts async and update
              searchAccountsByPhone(notification.customerPhone).then(matchingAccounts => {
                if (matchingAccounts.length > 0) {
                  const primaryAccount = matchingAccounts[0];
                  setIncomingCalls(current => current.map(c =>
                    c.chatId === notification.chatId ? {
                      ...c,
                      customerName: primaryAccount.name || c.customerName,
                      accountId: primaryAccount.id,
                      accountName: primaryAccount.name,
                      accountType: primaryAccount.type,
                      contactAccountId: primaryAccount.accountId,
                      matchingAccounts: matchingAccounts
                    } : c
                  ));
                }
              });

              // Fetch last conversation info async and update
              fetchLastConversationInfo(notification.chatId).then(convInfo => {
                if (convInfo) {
                  setIncomingCalls(current => current.map(c =>
                    c.chatId === notification.chatId ? {
                      ...c,
                      lastConversationDate: convInfo.lastConversationDate,
                      lastAgentName: convInfo.lastAgentName
                    } : c
                  ));
                }
              });

              return [...prev, incoming];
            });
          }
        }
      }
    } catch (err) {
      console.error('Polling error:', err);
    }
  }, [currentUser]);

  // Register agent and start polling
  const connectWebSocket = useCallback(async () => {
    try {
      // stage 2: registering with a queue that has been dead since 2026-07-06.
      if (!BAILEYS_QUEUE_ALIVE) return;
      const response = await fetch(`${API_BASE}/api/whatsapp/agent/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agentId: currentUser?.email,
          agentName: currentUser?.firstName || currentUser?.name
        })
      });
      const data = await response.json();
      if (data.success) {
        console.log('Agent registered via HTTP polling');
        setWsConnected(true);
        if (!pollingRef.current) {
          // 2026-06-21 incident mitigation: 3s → 30s. With multiple agents
          // across the office, the 3s poll compounded into 18+ req/sec on
          // /api/whatsapp/status alone, contributing to a retry storm.
          // Page-visibility guard already exists in pollNotifications.
          // stage 2: the poller it drove returns immediately now; no timer.
        }
      }
    } catch (err) {
      console.error('Agent registration error:', err);
    }
  }, [currentUser, pollNotifications]);

  // Play notification sound — owner-spec 2026-06-16. Was Audio('/notification.mp3')
  // but the file never shipped, so every call silently failed via the .catch.
  // Switched to Web Audio API so the beep works anywhere with no asset.
  // Two short pulses (~150ms each, 880Hz + 700Hz) — distinctive enough to
  // notice without being annoying. AudioContext must be created on user
  // interaction; the popup itself is triggered by an incoming poll which
  // counts after the page is loaded + tab focused.
  const playNotificationSound = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const blip = (freq, startSec, durSec) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + startSec);
        gain.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + startSec + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + startSec + durSec);
        osc.connect(gain).connect(ctx.destination);
        osc.start(ctx.currentTime + startSec);
        osc.stop(ctx.currentTime + startSec + durSec);
      };
      blip(880, 0,    0.16);  // first chirp
      blip(700, 0.18, 0.16);  // lower second chirp
      // Close the context shortly after the last blip so the tab doesn't
      // hold idle audio resources between calls.
      setTimeout(() => { try { ctx.close(); } catch {} }, 600);
    } catch (err) { /* swallow — never block the popup on audio */ }
  };

  // Handle connect/disconnect
  const handleConnect = (silent = false) => {
    setIsConnected(true);
    localStorage.setItem(`agent_status_${currentUser?.email}`, 'connected');
    setShowStatusMenu(false);

    if (!silent) {
      showToastMessage('הנך מחובר לתור WhatsApp');
    }

    connectWebSocket();
  };

  const handleDisconnect = () => {
    setIsConnected(false);
    localStorage.setItem(`agent_status_${currentUser?.email}`, 'disconnected');
    setShowStatusMenu(false);

    if (wsRef.current) {
      wsRef.current.close();
    }

    showToastMessage('התנתקת מתור WhatsApp');
  };

  // Show toast message
  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  // Handle answer call
  const handleAnswer = (call) => {
    console.log('[AGENT] Answering call:', call);
    setShowIncomingPopup(false);
    setIncomingCalls(prev => prev.filter(c => c.id !== call.id));

    // Always open chat modal first - allows agent to chat and optionally link to account
    console.log('[AGENT] Opening chat modal');
    const chatData = {
      chatId: call.chatId,
      customerName: call.customerName,
      customerPhone: call.customerPhone,
      message: call.message,
      accountId: call.accountId,
      accountName: call.accountName,
      accountType: call.accountType,
      // Pass all matching accounts for display
      matchingAccounts: call.matchingAccounts || [],
      timestamp: new Date().toISOString(),
      // Pass inquiry time for response time calculation
      inquiryTime: call.inquiryTime || call.timestamp,
      queueId: call.queueId,
      queueName: call.queueName
    };
    setModalChatData(chatData);
    setShowChatModal(true);

    // Add to recent chats with inquiry time for reporting
    addToRecentChats(chatData, 'active');
  };

  // Handle transfer (return to queue)
  const handleTransfer = async (call) => {
    try {
      await fetch(`${API_BASE}/api/whatsapp/transfer-to-queue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chatId: call.chatId,
          queueId: call.queueId
        })
      });

      setShowIncomingPopup(false);
      setIncomingCalls(prev => prev.filter(c => c.id !== call.id));

      // Track transferred calls in recent chats for reporting
      addToRecentChats({
        ...call,
        wasTransferred: true
      }, 'waiting');

      showToastMessage('השיחה הוחזרה לתור');
    } catch (err) {
      console.error('Transfer error:', err);
    }
  };

  // Handle right-click on communication center button
  const handleContextMenu = (e) => {
    e.preventDefault();
    setShowStatusMenu(true);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) &&
          buttonRef.current && !buttonRef.current.contains(e.target)) {
        setShowStatusMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load queues on mount
  useEffect(() => {
    loadUserQueues();
  }, [loadUserQueues]);

  // Start polling when connected
  useEffect(() => {
    if (isConnected && currentUser?.email) {
      console.log('[AGENT] Starting polling for', currentUser.email);
      // Register agent and poll immediately
      // stage 2: registering with a queue that has been dead since 2026-07-06.
      if (!BAILEYS_QUEUE_ALIVE) return;
      fetch(`${API_BASE}/api/whatsapp/agent/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agentId: currentUser.email,
          agentName: currentUser.firstName || currentUser.name
        })
      }).then(res => res.json()).then(data => {
        console.log('[AGENT] Registered:', data);
        if (data.waitingChats > 0) {
          console.log('[AGENT] Has waiting chats, polling now');
          pollNotifications();
        }
      });

      // Start interval if not already running
      if (!pollingRef.current) {
        // 2026-06-21 incident mitigation: 3s → 30s (see line ~488 above).
        // stage 2: the poller it drove returns immediately now; no timer.
      }
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [isConnected, currentUser, pollNotifications]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Handle logout - disconnect
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  return (
    <>
      {/* Bottom Banner - Communication Center */}
      <div className="slds-bottom-banner" style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: '48px',
        background: 'linear-gradient(135deg, #16325c 0%, #0d47a1 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 8000,
        boxShadow: '0 -2px 8px rgba(0,0,0,0.15)'
      }}>
        {/* Recent Chats Arrow */}
        <div style={{ position: 'relative', marginLeft: '16px' }}>
          <button
            onClick={() => {
              setShowRecentChatsModal(!showRecentChatsModal);
              setShowIncomingPopup(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              background: recentChats.filter(c => c.status === 'active' && c.agentId === currentUser?.email).length > 0 ? '#128c7e' : 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '50%',
              color: 'white',
              fontSize: '16px',
              cursor: 'pointer',
              position: 'relative'
            }}
            title="שיחות אחרונות"
          >
            💬
            {recentChats.filter(c => c.status === 'active').length > 0 && (
              <span style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                background: '#128c7e',
                color: 'white',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                fontSize: '11px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold'
              }}>
                {recentChats.filter(c => c.status === 'active' && c.agentId === currentUser?.email).length}
              </span>
            )}
          </button>

          {/* Omnichannel Panel with Tabs */}
          {showRecentChatsModal && (() => {
            const myChats = recentChats.filter(c => c.status === 'active' && c.agentId === currentUser?.email);
            const newChats = incomingCalls || [];
            return (
            <div style={{
              position: 'absolute',
              bottom: '50px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
              minWidth: '380px',
              maxWidth: '420px',
              maxHeight: '520px',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 9999
            }}>
              {/* Header */}
              <div style={{
                padding: '10px 16px',
                background: '#128c7e',
                borderRadius: '12px 12px 0 0',
                fontWeight: '600',
                color: 'white',
                textAlign: 'right',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <button
                  onClick={() => setShowRecentChatsModal(false)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >×</button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>💬</span>
                  <span>ריבוי ערוצים</span>
                  <span style={{
                    width: '8px', height: '8px', borderRadius: '50%',
                    background: isConnected ? '#4CAF50' : '#f44336'
                  }}></span>
                </div>
              </div>

              {/* Status Bar */}
              <div style={{
                padding: '6px 16px',
                background: '#f5f5f5',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '12px'
              }}>
                <span style={{ color: isConnected ? '#4CAF50' : '#f44336', fontWeight: '600' }}>
                  {isConnected ? 'Available - Messaging' : 'Offline'}
                </span>
                <button
                  onClick={() => {
                    setShowRecentChatsModal(false);
                    navigate('/whatsapp-reports');
                  }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#1976d2', fontSize: '12px', fontWeight: '500'
                  }}
                >📊 דוחות</button>
              </div>

              {/* Tabs */}
              <div style={{
                display: 'flex',
                borderBottom: '2px solid #e0e0e0'
              }}>
                <button
                  onClick={() => setPanelTab('new')}
                  style={{
                    flex: 1,
                    padding: '10px 8px',
                    border: 'none',
                    background: panelTab === 'new' ? 'white' : '#f9f9f9',
                    borderBottom: panelTab === 'new' ? '2px solid #128c7e' : '2px solid transparent',
                    fontWeight: panelTab === 'new' ? '700' : '400',
                    color: panelTab === 'new' ? '#128c7e' : '#666',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  חדש ({newChats.length})
                </button>
                <button
                  onClick={() => setPanelTab('mywork')}
                  style={{
                    flex: 1,
                    padding: '10px 8px',
                    border: 'none',
                    background: panelTab === 'mywork' ? 'white' : '#f9f9f9',
                    borderBottom: panelTab === 'mywork' ? '2px solid #128c7e' : '2px solid transparent',
                    fontWeight: panelTab === 'mywork' ? '700' : '400',
                    color: panelTab === 'mywork' ? '#128c7e' : '#666',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  העבודה שלי ({myChats.length})
                </button>
              </div>

              {/* Tab Content */}
              <div style={{ flex: 1, overflowY: 'auto', maxHeight: '380px' }}>
                {panelTab === 'new' ? (
                  /* === NEW TAB — incoming calls / pending === */
                  newChats.length === 0 ? (
                    <div style={{ padding: '32px 16px', textAlign: 'center', color: '#999' }}>
                      <div style={{ fontSize: '36px', marginBottom: '8px' }}>📭</div>
                      <div>אין לך בקשות פעילות.</div>
                    </div>
                  ) : (
                    newChats.map(call => {
                      // Auto-search account by phone
                      if (call.customerPhone && !chatAccountResults[call.chatId || call.id] && !accountSearching[call.chatId || call.id]) {
                        lookupAccountForChat(call.chatId || call.id, call.customerPhone);
                      }
                      const accounts = chatAccountResults[call.chatId || call.id];
                      const searching = accountSearching[call.chatId || call.id];

                      return (
                        <div key={call.id} style={{
                          padding: '12px 16px',
                          borderBottom: '1px solid #f0f0f0',
                          background: 'white'
                        }}>
                          {/* Phone + queue */}
                          <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            marginBottom: '6px'
                          }}>
                            <span style={{ fontSize: '15px', fontWeight: '700', color: '#1565c0', direction: 'ltr' }}>
                              📱 {call.customerPhone || 'לא ידוע'}
                            </span>
                            <span style={{ fontSize: '11px', color: '#888' }}>
                              {call.queueName || 'תור כללי'}
                            </span>
                          </div>

                          {/* Account match results */}
                          {searching ? (
                            <div style={{ fontSize: '12px', color: '#999', marginBottom: '6px' }}>מחפש חשבון...</div>
                          ) : accounts && accounts.length > 0 ? (
                            <div style={{ marginBottom: '6px' }}>
                              {accounts.map((acc, idx) => (
                                <div key={idx} style={{
                                  display: 'flex', alignItems: 'center', gap: '6px',
                                  padding: '4px 8px', background: '#e8f5e9', borderRadius: '4px', marginBottom: '2px'
                                }}>
                                  <span style={{
                                    background: acc.type === 'Contact' ? '#e1bee7' : '#bbdefb',
                                    color: acc.type === 'Contact' ? '#7b1fa2' : '#1565c0',
                                    padding: '1px 5px', borderRadius: '3px', fontSize: '10px'
                                  }}>
                                    {acc.type === 'Contact' ? 'איש קשר' : 'חשבון'}
                                  </span>
                                  <span style={{ fontWeight: '500', fontSize: '13px' }}>{acc.name}</span>
                                </div>
                              ))}
                            </div>
                          ) : accounts && accounts.length === 0 ? (
                            <div style={{
                              fontSize: '12px', color: '#e65100', marginBottom: '6px',
                              padding: '4px 8px', background: '#fff3e0', borderRadius: '4px'
                            }}>
                              ⚠️ לא קיים זיהוי מספר במערכת
                            </div>
                          ) : null}

                          {/* Contact name if available */}
                          {call.customerName && call.customerName !== call.customerPhone && (
                            <div style={{ fontSize: '12px', color: '#555', marginBottom: '6px' }}>
                              {call.customerName}
                            </div>
                          )}

                          {/* Message preview */}
                          {call.message && (
                            <div style={{
                              background: '#f5f5f5', padding: '6px 10px', borderRadius: '6px',
                              fontSize: '12px', marginBottom: '8px', maxHeight: '40px',
                              overflow: 'hidden', textOverflow: 'ellipsis', color: '#555'
                            }}>
                              {call.message}
                            </div>
                          )}

                          {/* Actions */}
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => handleTransfer(call)}
                              style={{
                                padding: '6px 12px', background: '#f5f5f5', border: 'none',
                                borderRadius: '6px', color: '#333', fontSize: '12px', cursor: 'pointer'
                              }}
                            >העברה לתור</button>
                            <button
                              onClick={() => {
                                // If accounts found, attach first account to the call
                                const accs = chatAccountResults[call.chatId || call.id] || call.matchingAccounts || [];
                                if (accs.length === 1) {
                                  call.accountId = accs[0].id;
                                  call.accountName = accs[0].name;
                                  call.matchingAccounts = accs;
                                } else if (accs.length > 1) {
                                  call.matchingAccounts = accs;
                                }
                                handleAnswer(call);
                              }}
                              style={{
                                padding: '6px 14px', background: '#4CAF50', border: 'none',
                                borderRadius: '6px', color: 'white', fontSize: '12px',
                                fontWeight: '600', cursor: 'pointer'
                              }}
                            >מענה 📞</button>
                          </div>
                        </div>
                      );
                    })
                  )
                ) : (
                  /* === MY WORK TAB — active conversations === */
                  myChats.length === 0 ? (
                    <div style={{ padding: '32px 16px', textAlign: 'center', color: '#999' }}>
                      <div style={{ fontSize: '36px', marginBottom: '8px' }}>📋</div>
                      <div>אין שיחות פעילות</div>
                    </div>
                  ) : (
                    myChats.map(chat => {
                      const hoursAgo = Math.floor((Date.now() - new Date(chat.startTime).getTime()) / (60 * 60 * 1000));
                      // Auto-search account
                      if (chat.customerPhone && !chatAccountResults[chat.chatId] && !accountSearching[chat.chatId]) {
                        lookupAccountForChat(chat.chatId, chat.customerPhone);
                      }
                      const accounts = chatAccountResults[chat.chatId];

                      return (
                        <div key={chat.chatId} style={{
                          padding: '12px 16px',
                          borderBottom: '1px solid #f0f0f0',
                          background: '#e8f5e9'
                        }}>
                          {/* Header */}
                          <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            marginBottom: '6px'
                          }}>
                            <span style={{
                              padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
                              fontWeight: '600', background: '#4CAF50', color: 'white'
                            }}>פעיל</span>
                            <span style={{ fontSize: '11px', color: '#706e6b' }}>
                              {hoursAgo < 1 ? 'עכשיו' : `לפני ${hoursAgo} שעות`}
                            </span>
                          </div>

                          {/* Phone */}
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px'
                          }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', direction: 'ltr' }}>
                              📱 {chat.customerPhone || 'לא ידוע'}
                            </span>
                            {chat.customerName && chat.customerName !== 'לקוח' && (
                              <span style={{ fontSize: '12px', color: '#706e6b' }}>({chat.customerName})</span>
                            )}
                          </div>

                          {/* Account info */}
                          {chat.accountName ? (
                            <div style={{ fontSize: '12px', color: '#1565c0', marginBottom: '6px' }}>
                              🏢 {chat.accountName}
                            </div>
                          ) : accounts && accounts.length > 0 ? (
                            <div style={{ marginBottom: '6px' }}>
                              {accounts.slice(0, 2).map((acc, idx) => (
                                <div key={idx} style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  padding: '2px 6px', background: '#e3f2fd', borderRadius: '4px',
                                  fontSize: '11px', marginLeft: '4px', color: '#1565c0'
                                }}>
                                  🏢 {acc.name}
                                </div>
                              ))}
                            </div>
                          ) : accounts && accounts.length === 0 ? (
                            <div style={{ fontSize: '11px', color: '#e65100', marginBottom: '6px' }}>
                              ⚠️ לא קיים זיהוי מספר במערכת
                            </div>
                          ) : null}

                          {/* Actions */}
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => handleEndChat(chat.chatId)}
                              style={{
                                padding: '6px 10px', background: '#f44336', color: 'white',
                                border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer'
                              }}
                            >סיום שיחה</button>
                            <button
                              onClick={() => handleReopenChat(chat)}
                              style={{
                                padding: '6px 10px', background: '#128c7e', color: 'white',
                                border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer'
                              }}
                            >פתח שיחה</button>
                          </div>
                        </div>
                      );
                    })
                  )
                )}

                {/* Recent ended chats (show in both tabs below) */}
                {recentChats.filter(c => c.status === 'ended' && c.agentId === currentUser?.email).length > 0 && panelTab === 'mywork' && (
                  <>
                    <div style={{
                      padding: '8px 16px', background: '#f5f5f5', borderTop: '1px solid #e0e0e0',
                      fontSize: '12px', fontWeight: '600', color: '#888'
                    }}>שיחות שהסתיימו</div>
                    {recentChats.filter(c => c.status === 'ended' && c.agentId === currentUser?.email).slice(0, 10).map(chat => {
                      const canReopen = canReopenChat(chat);
                      const hoursAgo = Math.floor((Date.now() - new Date(chat.startTime).getTime()) / (60 * 60 * 1000));
                      return (
                        <div key={chat.chatId} style={{
                          padding: '10px 16px', borderBottom: '1px solid #f0f0f0',
                          background: 'white', opacity: 0.8
                        }}>
                          <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            marginBottom: '4px'
                          }}>
                            <span style={{ fontSize: '13px', fontWeight: '600', direction: 'ltr' }}>
                              📱 {chat.customerPhone || 'לא ידוע'}
                            </span>
                            <span style={{ fontSize: '11px', color: '#999' }}>
                              {hoursAgo < 1 ? 'עכשיו' : hoursAgo < 24 ? `לפני ${hoursAgo} שע'` : 'מעל 24שע'}
                            </span>
                          </div>
                          {chat.accountName && (
                            <div style={{ fontSize: '11px', color: '#1565c0', marginBottom: '4px' }}>🏢 {chat.accountName}</div>
                          )}
                          {canReopen && (
                            <div style={{ textAlign: 'left' }}>
                              <button
                                onClick={() => handleReopenChat(chat)}
                                style={{
                                  padding: '4px 10px', background: '#128c7e', color: 'white',
                                  border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer'
                                }}
                              >פתח מחדש</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
            );
          })()}
        </div>

        {/* Availability Toggle */}
        <button
          onClick={() => {
            if (isConnected) {
              handleDisconnect();
            } else {
              handleConnect();
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 14px',
            background: isConnected ? 'rgba(76,175,80,0.3)' : 'rgba(244,67,54,0.3)',
            border: `1px solid ${isConnected ? '#4CAF50' : '#f44336'}`,
            borderRadius: '20px',
            color: 'white',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            marginLeft: '8px'
          }}
          title={isConnected ? 'לחץ להפוך ללא זמין' : 'לחץ להפוך לזמין'}
        >
          <span style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: isConnected ? '#4CAF50' : '#f44336',
            boxShadow: isConnected ? '0 0 6px #4CAF50' : 'none',
            transition: 'all 0.2s ease'
          }} />
          <span>{isConnected ? 'זמין' : 'לא זמין'}</span>
        </button>

        {/* Incoming Calls Arrow */}
        <div style={{ position: 'relative', marginLeft: '8px' }}>
          <button
            onClick={() => {
              setShowIncomingPopup(!showIncomingPopup);
              setShowRecentChatsModal(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              background: incomingCalls.length > 0 ? '#4CAF50' : 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '50%',
              color: 'white',
              fontSize: '16px',
              cursor: 'pointer',
              position: 'relative',
              animation: incomingCalls.length > 0 ? 'pulse 1.5s infinite' : 'none'
            }}
            title="שיחות נכנסות"
          >
            ▲
            {incomingCalls.length > 0 && (
              <span style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                background: '#f44336',
                color: 'white',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                fontSize: '11px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold'
              }}>
                {incomingCalls.length}
              </span>
            )}
          </button>

          {/* Incoming Calls Popup */}
          {showIncomingPopup && incomingCalls.length > 0 && (
            <div style={{
              position: 'absolute',
              bottom: '50px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
              minWidth: '300px',
              maxHeight: '400px',
              overflowY: 'auto',
              zIndex: 9999
            }}>
              <div style={{
                padding: '12px 16px',
                borderBottom: '1px solid #e5e5e5',
                background: '#f5f5f5',
                borderRadius: '12px 12px 0 0',
                fontWeight: '600',
                color: '#181818',
                textAlign: 'right'
              }}>
                📞 שיחות נכנסות ({incomingCalls.length})
              </div>

              {incomingCalls.map(call => (
                <div key={call.id} style={{
                  padding: '16px',
                  borderBottom: '1px solid #f0f0f0',
                  animation: 'slideIn 0.3s ease'
                }}>
                  {/* Phone number - prominently displayed */}
                  <div style={{
                    background: '#e3f2fd',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <span style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#1565c0',
                      direction: 'ltr'
                    }}>
                      📱 {call.customerPhone || 'לא ידוע'}
                    </span>
                    <span style={{ color: '#706e6b', fontSize: '12px' }}>
                      {call.queueName || 'תור כללי'}
                    </span>
                  </div>

                  {/* Matching accounts section */}
                  {call.matchingAccounts?.length > 0 ? (
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ fontSize: '12px', color: '#706e6b', marginBottom: '6px' }}>
                        {call.matchingAccounts.length === 1 ? 'חשבון תואם:' : `${call.matchingAccounts.length} חשבונות תואמים:`}
                      </div>
                      {call.matchingAccounts.map((acc, idx) => (
                        <div key={idx} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '4px 8px',
                          background: idx === 0 ? '#e8f5e9' : '#f5f5f5',
                          borderRadius: '4px',
                          marginBottom: '4px'
                        }}>
                          <span style={{
                            background: acc.type === 'Contact' ? '#e1bee7' : '#bbdefb',
                            color: acc.type === 'Contact' ? '#7b1fa2' : '#1565c0',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px'
                          }}>
                            {acc.type === 'Contact' ? 'איש קשר' : 'חשבון'}
                          </span>
                          <span style={{ fontWeight: '600', color: '#181818', fontSize: '13px' }}>
                            {acc.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '10px'
                    }}>
                      <span style={{ fontWeight: '600', color: '#181818' }}>
                        {call.customerName}
                      </span>
                      <span style={{
                        background: '#fff3e0',
                        color: '#e65100',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '11px'
                      }}>
                        לקוח חדש
                      </span>
                    </div>
                  )}

                  {call.message && (
                    <div style={{
                      background: '#f5f5f5',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      marginBottom: '12px',
                      maxHeight: '60px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {call.message}
                    </div>
                  )}

                  {/* Last conversation info */}
                  {call.lastConversationDate && (
                    <div style={{
                      background: '#e8f5e9',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      marginBottom: '12px',
                      borderRight: '3px solid #4CAF50'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#333' }}>
                        <span>💬</span>
                        <span>שיחה אחרונה:</span>
                        <strong>{new Date(call.lastConversationDate).toLocaleDateString('he-IL', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</strong>
                      </div>
                      {call.lastAgentName && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#666', marginTop: '4px' }}>
                          <span>👤</span>
                          <span>נציג:</span>
                          <strong style={{ color: '#128c7e' }}>{call.lastAgentName}</strong>
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'flex-end'
                  }}>
                    <button
                      onClick={() => handleTransfer(call)}
                      style={{
                        padding: '8px 16px',
                        background: '#f5f5f5',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#181818',
                        fontSize: '13px',
                        cursor: 'pointer'
                      }}
                    >
                      העברה לתור
                    </button>
                    <button
                      onClick={() => handleAnswer(call)}
                      style={{
                        padding: '8px 16px',
                        background: '#4CAF50',
                        border: 'none',
                        borderRadius: '6px',
                        color: 'white',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      מענה 📞
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Communication Center Button - Admin only */}
        {isAdmin && (
          <button
            ref={buttonRef}
            onClick={() => onNavigate ? onNavigate('/communication-center') : navigate('/communication-center')}
            onContextMenu={handleContextMenu}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '8px 24px',
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '24px',
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              position: 'relative'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <span style={{ fontSize: '18px' }}>💬</span>
            <span>מרכז תקשורת</span>

            {/* Status indicator */}
            <span style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: isConnected ? '#4CAF50' : '#f44336',
              boxShadow: isConnected ? '0 0 8px #4CAF50' : '0 0 8px #f44336',
              marginRight: '4px'
            }} />
          </button>
        )}

        {/* Status Context Menu */}
        {showStatusMenu && (
          <div
            ref={menuRef}
            style={{
              position: 'absolute',
              bottom: '56px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              minWidth: '180px',
              overflow: 'hidden',
              zIndex: 9999
            }}
          >
            <div style={{
              padding: '8px 12px',
              borderBottom: '1px solid #e5e5e5',
              fontSize: '12px',
              color: '#706e6b',
              textAlign: 'right'
            }}>
              סטטוס נציג
            </div>

            <button
              onClick={handleConnect}
              disabled={isConnected}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: isConnected ? '#e8f5e9' : 'white',
                border: 'none',
                cursor: isConnected ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                textAlign: 'right',
                direction: 'rtl'
              }}
            >
              <span style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: '#4CAF50'
              }} />
              <span style={{ color: '#181818', fontWeight: isConnected ? '600' : '400' }}>
                מחובר
              </span>
              {isConnected && <span style={{ marginRight: 'auto', color: '#4CAF50' }}>✓</span>}
            </button>

            <button
              onClick={handleDisconnect}
              disabled={!isConnected}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: !isConnected ? '#ffebee' : 'white',
                border: 'none',
                cursor: !isConnected ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                textAlign: 'right',
                direction: 'rtl'
              }}
            >
              <span style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: '#f44336'
              }} />
              <span style={{ color: '#181818', fontWeight: !isConnected ? '600' : '400' }}>
                לא מחובר
              </span>
              {!isConnected && <span style={{ marginRight: 'auto', color: '#f44336' }}>✓</span>}
            </button>

            {userQueues.length > 0 && (
              <div style={{
                padding: '8px 12px',
                borderTop: '1px solid #e5e5e5',
                fontSize: '11px',
                color: '#706e6b',
                textAlign: 'right'
              }}>
                תורים: {userQueues.map(q => q.name).join(', ')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: isConnected ? '#4CAF50' : '#f44336',
          color: 'white',
          padding: '20px 40px',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          zIndex: 99999,
          fontSize: '16px',
          fontWeight: '600',
          animation: 'fadeInOut 3s ease'
        }}>
          {toastMessage}
        </div>
      )}

      {/* Minimized Chat Indicator */}
      {minimizedChat && !showChatModal && (
        <button
          onClick={() => {
            setModalChatData(minimizedChat);
            setShowChatModal(true);
          }}
          style={{
            position: 'fixed',
            bottom: '56px',
            left: '50%',
            transform: 'translateX(calc(-50% + 180px))',
            background: '#128c7e',
            color: 'white',
            border: 'none',
            borderRadius: '20px',
            padding: '8px 16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            zIndex: 8001,
            animation: 'pulse 2s infinite'
          }}
        >
          <span>💬</span>
          <span>{minimizedChat.customerName}</span>
          <span style={{ fontSize: '10px', opacity: 0.8 }}>שיחה פעילה</span>
        </button>
      )}

      {/* Chat Modal for new customers (no Account found) */}
      {showChatModal && modalChatData && (
        <ChatModal
          chatData={modalChatData}
          onClose={() => {
            setShowChatModal(false);
            setModalChatData(null);
            setMinimizedChat(null); // Close completely
          }}
          onMinimize={() => {
            setMinimizedChat(modalChatData);
            setShowChatModal(false);
          }}
          onEndChat={() => {
            handleEndChat(modalChatData?.chatId);
            setShowChatModal(false);
            setModalChatData(null);
            setMinimizedChat(null);
          }}
          onLinkToRecord={async (recordType, recordId) => {
            console.log('[AGENT] Linking chat to', recordType, recordId);
            // Save conversation history to PostgreSQL
            try {
              const accountId = recordType === 'Account' ? recordId : null;
              const opportunityId = recordType === 'Opportunity' ? recordId : null;
              await fetch(`${API_BASE}/api/whatsapp/archive-chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  chatId: modalChatData?.chatId,
                  accountId,
                  opportunityId
                })
              });
              console.log('[AGENT] Chat history saved to PostgreSQL');
            } catch (err) {
              console.error('[AGENT] Error saving chat history:', err);
            }
            // Save link and navigate. The canonical record route is
            // /object/:objectName/:recordId (see routes.js + App.js Route).
            // The old `/objects/${type}/records/${id}` had no matching
            // Route and silently fell through to the catch-all (home).
            setShowChatModal(false);
            setModalChatData(null);
            setMinimizedChat(null);
            navigate(buildRecordUrl(recordType, recordId));
          }}
          navigate={navigate}
          currentUser={currentUser}
        />
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes fadeInOut {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
          15% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          85% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
        }
      `}</style>
    </>
  );
};

// Message templates — owner-spec 2026-06-16: fetched live from
// /api/whatsapp/quick-templates/active. Admin: הגדרות → תבניות הודעות WhatsApp.
const renderTemplate = (rawText, userName) =>
  String(rawText || '').replace(/\{userName\}/g, userName || '');

// Chat Modal Component for new customers
const ChatModal = ({ chatData, onClose, onMinimize, onEndChat, onLinkToRecord, navigate, currentUser }) => {
  const [showLinkSearch, setShowLinkSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  // 2026-06-16: active templates fetched from /api/whatsapp/quick-templates/active.
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    if (!showTemplates) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/whatsapp/quick-templates/active`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setTemplates(data.templates || []);
      } catch (e) { /* keep prior list */ }
    })();
    return () => { cancelled = true; };
  }, [showTemplates]);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [accountModalInitialData, setAccountModalInitialData] = useState(null);
  const messagesEndRef = useRef(null);

  // Phase 1 (owner-spec 2026-05-31): media library + lightbox + in-chat
  // search shared with WhatsAppChat.js via ./chat/*. Local state lives
  // here because the modal has its own messages array — the drawer +
  // lightbox derive from it on render.
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [chatSearchOpen, setChatSearchOpen] = useState(false);
  const [chatSearchQuery, setChatSearchQuery] = useState('');
  const [chatSearchMatchIdx, setChatSearchMatchIdx] = useState(0);

  const lightboxMedia = useMemo(() => buildMediaList(messages), [messages]);
  // Search matches keyed by msg.id (stable across re-renders, works
  // independently of how the render groups messages by date).
  const chatSearchMatches = useMemo(() => {
    const q = (chatSearchQuery || '').trim().toLowerCase();
    if (!q) return [];
    return messages
      .filter(m => (m.body || '').toLowerCase().includes(q))
      .map(m => m.id)
      .filter(Boolean);
  }, [messages, chatSearchQuery]);
  const messageRefs = useRef({});

  useEffect(() => { setChatSearchMatchIdx(0); }, [chatSearchMatches.length]);

  useEffect(() => {
    if (!chatSearchMatches.length) return;
    const targetId = chatSearchMatches[chatSearchMatchIdx];
    const el = messageRefs.current[targetId];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [chatSearchMatchIdx, chatSearchMatches]);

  const openLightboxForMsg = useCallback((msg) => {
    const idx = lightboxMedia.findIndex(it => it.id === msg.id);
    setLightbox({ open: true, index: Math.max(0, idx) });
  }, [lightboxMedia]);

  // Phase 2 (owner-spec 2026-05-31): reply state local to this modal.
  const [replyTarget, setReplyTarget] = useState(null);
  // Phase 3: message-info modal.
  const [infoModalMsg, setInfoModalMsg] = useState(null);
  const scrollToMessage = useCallback((id) => {
    const el = messageRefs.current && messageRefs.current[id];
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  // Load chat messages (only last 24 hours).
  // 2026-05-31 owner-flagged: the chat modal opened empty even when the
  // bot had clearly been conversing with the customer. Cause: the loader
  // gated `setMessages` on `if (data.success)` — but the
  // /api/whatsapp/messages/{chatId} proxy returns plain
  //   { messages: [...] }
  // (forwarded verbatim from Baileys) with no `success` field. So the
  // condition was always falsy and `messages` stayed at its initial [].
  // Bot history + Infobip inbound messages WERE in messages.json all
  // along — the FE just refused to read them.
  useEffect(() => {
    const loadMessages = async () => {
      // 2026-06-21 — Page Visibility guard (see pollNotifications above).
      if (typeof document !== 'undefined' && document.hidden) {
        if (typeof window !== 'undefined') {
          window.__pollSkipCount = (window.__pollSkipCount || 0) + 1;
        }
        return;
      }
      try {
        const response = await fetch(`${API_BASE}/api/whatsapp/messages/${chatData.chatId}`);
        const data = await response.json();
        const raw = Array.isArray(data?.messages) ? data.messages : [];
        // Filter to only show last 24 hours.
        const twentyFourHoursAgo = Date.now() - (24 * 60 * 60 * 1000);
        const filteredMessages = raw.filter(msg => {
          // Timestamps come in two formats: epoch seconds (Baileys/Infobip
          // saves int(time.time())) and ISO strings (older paths). Handle
          // both, fail-open so a malformed ts doesn't drop the row.
          let ts = msg.timestamp;
          if (typeof ts === 'string') {
            const parsed = new Date(ts).getTime();
            return !isNaN(parsed) ? parsed >= twentyFourHoursAgo : true;
          }
          if (typeof ts === 'number') {
            const ms = ts < 1e12 ? ts * 1000 : ts; // seconds vs ms
            return ms >= twentyFourHoursAgo;
          }
          return true;
        });
        setMessages(filteredMessages);
      } catch (err) {
        console.error('Error loading messages:', err);
      }
    };
    loadMessages();

    // Poll for new messages
    // 2026-06-21 incident mitigation: 3s → 15s. Chat messages will appear
    // up to 15s late but agents can still chat — vs the retry storm pre-fix.
    const interval = setInterval(loadMessages, 15000);
    return () => clearInterval(interval);
  }, [chatData.chatId]);

  // 2026-05-31: open at the bottom on first messages-load (otherwise
  // the user lands on the OLDEST message — owner-flagged), then sticky-
  // scroll on subsequent updates.
  const didInitialScrollRef = useRef(false);
  useEffect(() => { didInitialScrollRef.current = false; }, [chatData.chatId]);
  useEffect(() => {
    const end = messagesEndRef.current;
    const container = end && end.parentElement;
    if (!container || messages.length === 0) return;
    if (!didInitialScrollRef.current) {
      container.scrollTop = container.scrollHeight;
      didInitialScrollRef.current = true;
      return;
    }
    const nearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 120;
    if (nearBottom) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  // Search for Account/Opportunity using global search API
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      // Use global search API
      const response = await fetch(`${MAIN_API_BASE}/api/search?q=${encodeURIComponent(searchQuery)}&limit=20`);
      const data = await response.json();

      console.log('[ChatModal] Search results:', data);

      const results = [];
      // Process search results - format: array of { object_name, records: [] }
      if (data.results && Array.isArray(data.results)) {
        for (const objResult of data.results) {
          const objectName = objResult.object_name;
          const records = objResult.records || [];

          // Only process Accounts and Opportunities
          if (objectName === 'Account') {
            for (const acc of records) {
              results.push({
                type: 'Account',
                id: acc.Id,
                name: acc.Name || acc._display_name || `${acc.FirstName || ''} ${acc.LastName || ''}`.trim(),
                description: acc.Phone || acc.BillingCity || ''
              });
            }
          } else if (objectName === 'Opportunity') {
            for (const opp of records) {
              results.push({
                type: 'Opportunity',
                id: opp.Id,
                name: opp.Name || opp._display_name,
                description: opp.StageName || opp.AccountName || ''
              });
            }
          }
        }
      }
      console.log('[ChatModal] Processed results:', results);
      setSearchResults(results);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  };

  // Send message
  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    const messageToSend = newMessage;
    setSending(true);
    try {
      console.log('[ChatModal] Sending message to:', chatData.chatId);
      const response = await fetch(`${API_BASE}/api/whatsapp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chatId: chatData.chatId,
          message: messageToSend,
          userId: currentUser?.email,
          userName: currentUser?.firstName || currentUser?.name,
          // Phase 2 owner-spec 2026-05-31.
          replyTo: replyTarget || undefined,
        })
      });
      const result = await response.json();
      console.log('[ChatModal] Send result:', result);

      if (result.success) {
        setNewMessage('');
        // Immediately add message to local state for instant feedback
        setMessages(prev => [...prev, {
          id: result.messageId || Date.now(),
          body: messageToSend,
          fromMe: true,
          timestamp: Math.floor(Date.now() / 1000),
          replyTo: replyTarget || undefined,
        }]);
        setReplyTarget(null);
      } else {
        console.error('[ChatModal] Send failed:', result.error);
        alert('שגיאה בשליחת ההודעה: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('[ChatModal] Send error:', err);
      alert('שגיאה בשליחת ההודעה');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 99999
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        width: '500px',
        maxWidth: '95vw',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          background: '#16325c',
          color: 'white'
        }}>
          {/* Phone number - prominently displayed */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px'
          }}>
            <div style={{
              background: 'rgba(255,255,255,0.2)',
              padding: '6px 14px',
              borderRadius: '20px',
              fontSize: '15px',
              fontWeight: '600',
              direction: 'ltr'
            }}>
              📱 {chatData.customerPhone || chatData.customerName || 'לא ידוע'}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {/* Phase 1 owner-spec 2026-05-31: in-chat search + media library */}
              <button
                onClick={() => setChatSearchOpen(s => !s)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
                title="חיפוש בצ'אט"
              >🔍</button>
              <button
                onClick={() => setLibraryOpen(true)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
                title="מדיה, מסמכים וקישורים"
              >📁</button>
              {onEndChat && (
                <button onClick={onEndChat} style={{
                  background: '#f44336',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600'
                }} title="סיום שיחה">סיים</button>
              )}
              <button onClick={onMinimize} style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                padding: '6px 10px',
                cursor: 'pointer',
                fontSize: '16px'
              }} title="מזער">─</button>
              <button onClick={onClose} style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                padding: '6px 10px',
                cursor: 'pointer',
                fontSize: '16px'
              }} title="סגור">×</button>
            </div>
          </div>

          {/* Matching accounts */}
          {chatData.matchingAccounts?.length > 0 ? (
            <div>
              <div style={{ fontSize: '11px', opacity: 0.7, marginBottom: '6px' }}>
                {chatData.matchingAccounts.length === 1 ? 'חשבון תואם:' : `${chatData.matchingAccounts.length} חשבונות תואמים - לחץ לפתיחה:`}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {chatData.matchingAccounts.map((acc, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      navigate(buildRecordUrl(acc.type, acc.id));
                      onClose();
                    }}
                    style={{
                      background: idx === 0 ? '#4CAF50' : 'rgba(255,255,255,0.2)',
                      border: 'none',
                      borderRadius: '6px',
                      color: 'white',
                      padding: '6px 12px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <span style={{
                      background: 'rgba(255,255,255,0.3)',
                      padding: '1px 4px',
                      borderRadius: '3px',
                      fontSize: '10px'
                    }}>
                      {acc.type === 'Contact' ? 'איש קשר' : 'חשבון'}
                    </span>
                    {acc.name}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontWeight: '600', fontSize: '16px' }}>{chatData.customerName}</span>
              <span style={{
                background: '#ff9800',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '11px'
              }}>
                לא קיים זיהוי מספר במערכת
              </span>
            </div>
          )}
        </div>


        {/* Phase 1 owner-spec 2026-05-31: in-chat search bar (toggled
            from header 🔍). Sits between header and message area like
            WhatsApp Web. */}
        <ChatSearchBar
          open={chatSearchOpen}
          query={chatSearchQuery}
          onQueryChange={setChatSearchQuery}
          onClose={() => { setChatSearchOpen(false); setChatSearchQuery(''); }}
          matchCount={chatSearchMatches.length}
          currentMatch={chatSearchMatches.length ? chatSearchMatchIdx + 1 : 0}
          onPrev={() => setChatSearchMatchIdx(i => Math.max(0, i - 1))}
          onNext={() => setChatSearchMatchIdx(i => Math.min(chatSearchMatches.length - 1, i + 1))}
        />

        {/* Messages */}
        <div style={{
          flex: 1,
          padding: '16px',
          overflowY: 'auto',
          background: '#e5ddd5',
          minHeight: '200px'
        }}>
          {(() => {
            // Group messages by date for separators
            let lastDate = null;
            return messages.map((msg, idx) => {
              const msgDate = new Date(msg.timestamp * 1000);
              const dateStr = msgDate.toLocaleDateString('he-IL');
              const showDateSeparator = dateStr !== lastDate;
              lastDate = dateStr;

              // Format date for display
              const today = new Date().toLocaleDateString('he-IL');
              const yesterday = new Date(Date.now() - 86400000).toLocaleDateString('he-IL');
              let displayDate = dateStr;
              if (dateStr === today) displayDate = 'היום';
              else if (dateStr === yesterday) displayDate = 'אתמול';
              else displayDate = msgDate.toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'long' });

              return (
                <React.Fragment key={idx}>
                  {/* Date separator */}
                  {showDateSeparator && (
                    <div style={{
                      textAlign: 'center',
                      margin: '12px 0'
                    }}>
                      <span style={{
                        background: 'rgba(225, 218, 208, 0.9)',
                        padding: '6px 16px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        color: '#54656f',
                        boxShadow: '0 1px 1px rgba(0,0,0,0.1)'
                      }}>
                        {displayDate}
                      </span>
                    </div>
                  )}

                  {/* Message */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: msg.fromMe ? 'flex-start' : 'flex-end',
                      marginBottom: '8px',
                      position: 'relative',
                    }}
                    onMouseEnter={(e) => {
                      const ic = e.currentTarget.querySelector('.wa-actions-trigger');
                      if (ic) ic.style.opacity = '1';
                    }}
                    onMouseLeave={(e) => {
                      const ic = e.currentTarget.querySelector('.wa-actions-trigger');
                      if (ic) ic.style.opacity = '0';
                    }}
                  >
                    {/* Phase 3 owner-spec 2026-05-31: action dropdown */}
                    <MessageActionsMenu
                      msg={msg}
                      align={msg.fromMe ? 'right' : 'left'}
                      onReply={() => setReplyTarget(buildReplyTarget(msg))}
                      onInfo={() => setInfoModalMsg(msg)}
                    />
                    <div
                      ref={el => { if (msg.id) messageRefs.current[msg.id] = el; }}
                      style={{
                        background: msg.fromMe ? '#dcf8c6' : 'white',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        maxWidth: '70%',
                        boxShadow: chatSearchMatches[chatSearchMatchIdx] === msg.id
                          ? '0 0 0 3px #ffc107'
                          : chatSearchMatches.includes(msg.id)
                            ? '0 0 0 2px rgba(255,193,7,0.45)'
                            : '0 1px 2px rgba(0,0,0,0.1)',
                        transition: 'box-shadow 0.18s ease',
                      }}
                    >
                      {/* Sender name for outgoing messages */}
                      {msg.fromMe && msg.sentByName && (
                        <div style={{
                          fontSize: '11px',
                          color: '#128c7e',
                          fontWeight: '600',
                          marginBottom: '4px'
                        }}>
                          {msg.sentByName}
                        </div>
                      )}
                      {/* Phase 2: reply quote chip when this msg replies to another */}
                      {msg.replyTo && (
                        <ReplyQuote
                          replyTo={msg.replyTo}
                          messages={messages}
                          onClickOriginal={scrollToMessage}
                        />
                      )}
                      {/* Phase 1 owner-spec 2026-05-31: media renderer
                          (shared with WhatsAppChat.js via ./chat/MessageBubble).
                          Falls through to plain text for non-media. */}
                      {(() => {
                        const t = (msg.mediaType || msg.type || '').toLowerCase();
                        if (['image', 'video', 'audio', 'voice', 'document'].includes(t)) {
                          return renderMedia(msg, openLightboxForMsg);
                        }
                        return (
                          <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{msg.body}</div>
                        );
                      })()}
                      <div style={{
                        fontSize: '10px',
                        color: '#999',
                        marginTop: '4px',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <span>{msgDate.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}</span>
                        {msg.fromMe && <span>{msg.ack >= 2 ? '✓✓' : '✓'}</span>}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            });
          })()}
          <div ref={messagesEndRef} />
        </div>

        {/* Templates Popup */}
        {showTemplates && (
          <div style={{
            padding: '12px',
            background: '#fff',
            borderTop: '1px solid #e5e5e5',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            {templates.length === 0 ? (
              <div style={{ padding: 10, fontSize: 12, color: '#888', textAlign: 'center', width: '100%' }}>
                אין תבניות פעילות (הגדרות → תבניות הודעות WhatsApp).
              </div>
            ) : templates.map(template => (
              <button
                key={template.id}
                onClick={() => {
                  const userName = currentUser?.firstName || currentUser?.name || 'נציג';
                  setNewMessage(renderTemplate(template.text, userName));
                  setShowTemplates(false);
                }}
                style={{
                  padding: '8px 12px',
                  background: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <span>{template.name}</span>
              </button>
            ))}
          </div>
        )}

        {/* Phase 2 owner-spec 2026-05-31: reply composer bar */}
        <ReplyComposerBar replyTarget={replyTarget} onCancel={() => setReplyTarget(null)} />

        {/* Input */}
        <div style={{
          padding: '12px 16px',
          background: '#f0f0f0',
          display: 'flex',
          gap: '8px',
          alignItems: 'center'
        }}>
          <button
            onClick={() => setShowTemplates(!showTemplates)}
            style={{
              background: showTemplates ? '#128c7e' : '#e0e0e0',
              color: showTemplates ? 'white' : '#666',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              cursor: 'pointer',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="תבניות"
          >
            📝
          </button>
          <input
            type="text"
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            placeholder="הקלד הודעה..."
            style={{
              flex: 1,
              padding: '10px 12px',
              border: 'none',
              borderRadius: '20px',
              fontSize: '14px'
            }}
          />
          <button onClick={handleSend} disabled={sending} style={{
            background: '#128c7e',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            cursor: 'pointer',
            fontSize: '16px',
            transform: 'scaleX(-1)' // Flip arrow to point left (RTL)
          }}>
            ➤
          </button>
        </div>

        {/* Link to Account/Opportunity section */}
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid #e5e5e5',
          background: '#fafafa'
        }}>
          {!showLinkSearch ? (
            <button
              onClick={() => setShowLinkSearch(true)}
              style={{
                width: '100%',
                padding: '10px',
                background: '#0d47a1',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              🔗 הצמד לחשבון או הזדמנות
            </button>
          ) : (
            <div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  placeholder="חפש חשבון או הזדמנות..."
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <button
                  onClick={handleSearch}
                  disabled={searching}
                  style={{
                    padding: '10px 16px',
                    background: '#0d47a1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  {searching ? '...' : 'חפש'}
                </button>
                <button
                  onClick={() => {
                    setShowLinkSearch(false);
                    setSearchResults([]);
                  }}
                  style={{
                    padding: '10px 12px',
                    background: '#f5f5f5',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  ×
                </button>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {searchResults.map((result, idx) => (
                    <div
                      key={idx}
                      onClick={() => onLinkToRecord(result.type, result.id)}
                      style={{
                        padding: '10px 12px',
                        borderBottom: '1px solid #f0f0f0',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                      onMouseOver={e => e.currentTarget.style.background = '#f5f5f5'}
                      onMouseOut={e => e.currentTarget.style.background = 'white'}
                    >
                      <div>
                        <div style={{ fontWeight: '600' }}>{result.name}</div>
                        <div style={{ fontSize: '12px', color: '#706e6b' }}>{result.description}</div>
                      </div>
                      <span style={{
                        background: result.type === 'Account' ? '#e3f2fd' : '#e8f5e9',
                        color: result.type === 'Account' ? '#1565c0' : '#2e7d32',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px'
                      }}>
                        {result.type === 'Account' ? 'חשבון' : 'הזדמנות'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Create New Account Button */}
              <button
                onClick={() => {
                  setAccountModalInitialData({
                    Phone: chatData.customerPhone || '',
                    WhatsApp_Chat_Id__c: chatData.chatId || '',
                    Name: chatData.customerName || ''
                  });
                  setShowAccountModal(true);
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  marginTop: '8px',
                  background: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                + צור חשבון חדש
              </button>
            </div>
          )}
        </div>
      </div>
      {showAccountModal && (
        <Suspense fallback={<div style={{ padding: '20px', textAlign: 'center' }}>טוען טופס...</div>}>
          <AccountModal
            isOpen={showAccountModal}
            onClose={() => { setShowAccountModal(false); setAccountModalInitialData(null); }}
            onSuccess={(newAccount) => {
              setShowAccountModal(false);
              setAccountModalInitialData(null);
              if (newAccount?.Id) {
                navigate(buildRecordUrl('Account', newAccount.Id));
                onClose();
              }
            }}
            initialData={accountModalInitialData}
            currentUser={currentUser}
          />
        </Suspense>
      )}

      {/* Phase 1 owner-spec 2026-05-31: shared media UI shells —
          same overlays as in WhatsAppChat.js. */}
      {lightbox.open && lightboxMedia.length > 0 && (
        <MediaLightbox
          mediaList={lightboxMedia}
          currentIndex={lightbox.index}
          onClose={() => setLightbox({ open: false, index: 0 })}
          onPrev={() => setLightbox(l => ({ ...l, index: Math.max(0, l.index - 1) }))}
          onNext={() => setLightbox(l => ({ ...l, index: Math.min(lightboxMedia.length - 1, l.index + 1) }))}
          onJumpTo={(i) => setLightbox({ open: true, index: i })}
        />
      )}
      <MediaLibraryDrawer
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        messages={messages}
        onMediaClick={(i) => {
          setLibraryOpen(false);
          setLightbox({ open: true, index: i });
        }}
      />
      {/* Phase 3: message-info modal */}
      <MessageInfoModal msg={infoModalMsg} onClose={() => setInfoModalMsg(null)} />
    </div>
  );
};

export default AgentStatusManager;
